import { css } from '@emotion/react';
import '@fontsource-variable/eb-garamond';
import '@fontsource/gudea/latin.css';
import { extendTheme } from '@mui/joy';
import { createTheme as materialExtendTheme } from '@mui/material';
import type {} from '@mui/x-date-pickers/themeAugmentation';

export const materialTheme = materialExtendTheme({
  palette: {
    primary: {
      50: '#FCFAE7',
      100: '#F6F1D9',
      200: '#E4D6AF',
      300: '#CAB37A',
      400: '#AF9145',
      500: '#846701',
      600: '#785D01',
      700: '#5F4A01',
      800: '#413208',
      900: '#241C06',
    },
    error: {
      main: '#C50F50',
    },
  },
  components: {
    MuiPickersTextField: {
      styleOverrides: {
        root: {
          '& .MuiPickersInputBase-root': {
            height: '40px',
          },
          backgroundColor: 'white',
        },
      },
    },
  },
});

export const theme = extendTheme({
  colorSchemes: {
    light: {
      palette: {
        primary: {
          50: '#FCFAE7',
          100: '#F6F1D9',
          200: '#E4D6AF',
          300: '#CAB37A',
          400: '#AF9145',
          500: '#846701',
          600: '#785D01',
          700: '#5F4A01',
          800: '#413208',
          900: '#241C06',
        },

        neutral: {
          50: '#fbfbfb',
          100: '#f0f0f0',
          200: '#dddddd',
          300: '#cdcdcd',
          400: '#9f9f9f',
          500: '#6d6d6d',
          600: '#555555',
          700: '#323232',
          800: '#171717',
          900: '#0B0B0B',
        },

        danger: {
          50: '#FEF4F5',
          100: '#FCDDE0',
          200: '#F5BCC2',
          300: '#E88896',
          400: '#DD687E',
          500: '#C50F50',
          600: '#AB013D',
          700: '#8D0028',
          800: '#700015',
          900: '#540000',
        },

        success: {
          50: '#F3F8F2',
          100: '#E6F0E4',
          200: '#B5D2B1',
          300: '#83B47C',
          400: '#438C3A',
          500: '#227817',
          600: '#1E7014',
          700: '#196511',
          800: '#145B0D',
          900: '#0C4807',
        },

        warning: {
          50: '#EDF1FA',
          100: '#DCE5F6',
          200: '#B9CBEC',
          300: '#8CAEE0',
          400: '#007AC9',
          500: '#1565A4',
          600: '#1B5081',
          700: '#1B3D60',
          800: '#182A40',
          900: '#121823',
        },

        text: {
          icon: '#6d6d6d',
          primary: '#171717',
          secondary: '#323232',
          tertiary: '#555555',
        },

        common: {
          white: '#FFFFFF',
          black: '#000000',
        },

        background: {
          body: 'var(--joy-palette-common-white)',
          surface: 'var(--joy-palette-neutral-50)',
          backdrop: 'rgba(218, 218, 218, 0.65)',
          level1: 'var(--joy-palette-neutral-100)',
        },
      },
    },
  },

  fontFamily: {
    body: '"Gudea", sans-serif',
    display: '"Gudea", sans-serif',
  },

  variants: {
    outlined: {
      neutral: {
        '--variant-borderWidth': '1px',
      },
      primary: {
        '--variant-borderWidth': '1px',
      },
      danger: {
        '--variant-borderWidth': '1px',
      },
      warning: {
        '--variant-borderWidth': '1px',
      },
      success: {
        '--variant-borderWidth': '1px',
      },
    },
  },

  shadow: {
    xs: '0px 1px 2px 0px rgba(21, 21, 21, 0.08)',
    sm: '0px 1px 2px 0px rgba(21, 21, 21, 0.08), 0px 2px 4px 0px rgba(21, 21, 21, 0.08)',
    md: '0px 2px 8px -2px rgba(21, 21, 21, 0.08), 0px 6px 12px -2px rgba(21, 21, 21, 0.08)',
    lg: '0px 2px 8px -2px rgba(21, 21, 21, 0.08), 0px 12px 16px -4px rgba(21, 21, 21, 0.08)',
    xl: '0px 2px 8px -2px rgba(21, 21, 21, 0.08), 0px 20px 24px -4px rgba(21, 21, 21, 0.08)',
  },

  typography: {
    h1: {
      fontFamily: '"Gudea", sans-serif',
      fontWeight: 700,
      fontSize: '1.5rem',
      lineHeight: '133%',
      color: 'var(--text-primary, #171717)',
    },
    h2: {
      fontFamily: '"Gudea", sans-serif',
      fontWeight: 700,
      fontSize: '1.25rem',
      lineHeight: '133%',
      color: 'var(--text-primary, #171717)',
    },
    h3: {
      fontFamily: '"Gudea", sans-serif',
      fontWeight: 700,
      fontSize: '1.125rem',
      lineHeight: '150%',
      color: 'var(--text-primary, #171717)',
    },
    h4: {
      fontFamily: '"Gudea", sans-serif',
      fontWeight: 700,
      fontSize: '1rem',
      lineHeight: '155%',
      color: 'var(--text-primary, #171717)',
    },
    'title-lg': {
      fontFamily: '"Gudea", sans-serif',
      fontWeight: 700,
      fontSize: '1.125rem',
      lineHeight: '166%',
      color: 'var(--text-primary, #171717)',
    },
    'title-md': {
      fontFamily: '"Gudea", sans-serif',
      fontWeight: 700,
      fontSize: '1rem',
      lineHeight: '150%',
      color: 'var(--text-primary, #171717)',
    },
    'title-sm': {
      fontFamily: '"Gudea", sans-serif',
      fontWeight: 700,
      fontSize: '.875rem',
      lineHeight: '142%',
      color: 'var(--text-primary, #171717)',
    },
    'body-lg': {
      fontFamily: '"Gudea", sans-serif',
      fontWeight: 400,
      fontSize: '1.125rem',
      lineHeight: '155%',
      color: 'var(--text-primary, #171717)',
    },
    'body-md': {
      fontFamily: '"Gudea", sans-serif',
      fontWeight: 400,
      fontSize: '1rem',
      lineHeight: '150%',
      color: 'var(--text-primary, #171717)',
    },
    'body-sm': {
      fontFamily: '"Gudea", sans-serif',
      fontWeight: 400,
      fontSize: '.875rem',
      lineHeight: '142%',
      color: 'var(--text-primary, #171717)',
    },
    'body-xs': {
      fontFamily: '"Gudea", sans-serif',
      fontWeight: 700,
      fontSize: '.75rem',
      lineHeight: '166%',
      color: 'var(--text-primary, #171717)',
    },
  },

  components: {
    JoyBox: {
      styleOverrides: {
        root: ({ theme: overrideTheme }) => css`
          background-color: ${overrideTheme.vars.palette.background.surface};
        `,
      },
    },

    JoyAlert: {
      styleOverrides: {
        root: ({ ownerState, theme: overrideTheme }) => css`
          ${
            ownerState.variant === 'outlined' &&
            css`
              --variant-borderWidth: 2px;
              color: ${overrideTheme.vars.palette.common.white};
              background-color: ${overrideTheme.vars.palette.danger['100']};
            `
          }
        `,
      },
    },

    JoyAutocomplete: {
      defaultProps: {
        size: 'lg',
        variant: 'plain',
      },
      styleOverrides: {
        root: ({ theme: overrideTheme, ownerState }) => css`
          background-color: ${overrideTheme.palette.common.white};

          ${
            ownerState.variant === 'outlined' &&
            css`
              box-shadow: none;
              --joy-focus-thickness: 2px;
            `
          }

          ${
            ownerState.size === 'lg' &&
            css`
              height: 48px;
            `
          }
        `,
      },
    },
    JoyButton: {
      defaultProps: {
        size: 'lg',
      },
      styleOverrides: {
        root: ({ ownerState }) => css`
          ${
            ownerState.size === 'lg' &&
            css`
              --Button-minHeight: 3rem;
            `
          }

          ${
            ownerState.size === 'lg' &&
            css`
              --Button-minHeight: 3rem;
              font-family: 'Gudea', sans-serif;
              font-size: 16px;
              font-style: normal;
              font-weight: 700;
              line-height: 16px;
            `
          }

          ${
            ownerState.size === 'md' &&
            css`
              --Button-minHeight: 2.5rem;
              font-family: 'Gudea', sans-serif;
              font-size: 14px;
              font-style: normal;
              font-weight: 700;
              line-height: 14px;
            `
          }

          ${
            ownerState.size === 'sm' &&
            css`
              --Button-minHeight: 2rem;
              font-family: 'Gudea', sans-serif;
              font-size: 14px;
              font-style: normal;
              font-weight: 700;
              line-height: 14px;
            `
          }
        `,
      },
    },
    JoyChip: {
      styleOverrides: {
        root: ({ ownerState }) => css`
          ${
            ownerState.size === 'lg' &&
            css`
              --Chip-minHeight: 2.5rem;
            `
          }
        `,

        action: ({ theme: overrideTheme, ownerState }) => css`
          ${
            ownerState.variant === 'outlined' &&
            css`
              --variant-borderWidth: 2px;
              --variant-outlinedBorder: ${overrideTheme.vars.palette.primary['500']};
            `
          }
        `,
      },
    },
    JoyFormControl: {
      defaultProps: {
        size: 'lg',
      },
    },
    JoyFormLabel: {
      styleOverrides: {
        root: css`
          font-weight: bold;
        `,
      },
    },
    JoyInput: {
      defaultProps: {
        size: 'lg',
        variant: 'plain',
      },
      styleOverrides: {
        root: ({ ownerState, theme: overrideTheme }) => css`
          background-color: ${overrideTheme.palette.common.white};
          ${
            ownerState.variant === 'outlined' &&
            css`
              box-shadow: none;
              --joy-focus-thickness: 2px;
            `
          }

          ${
            ownerState.size === 'lg' &&
            css`
              --Input-minHeight: 3rem;
              font-size: 18px;
              font-style: normal;
              font-weight: 400;
              line-height: 24px;
            `
          }

          ${
            ownerState.size === 'md' &&
            css`
              --Input-minHeight: 2.5rem;
              font-family: 'Gudea', sans-serif;
              font-size: 16px;
              font-style: normal;
              font-weight: 400;
              line-height: 20px;
            `
          }

          ${
            ownerState.size === 'sm' &&
            css`
              --Input-minHeight: 2rem;
              font-family: 'Gudea', sans-serif;
              font-size: 14px;
              font-style: normal;
              font-weight: 400;
              line-height: 16px;
            `
          }

          ${
            ownerState.readOnly &&
            css`
              border: 1px solid ${overrideTheme.vars.palette.neutral.outlinedBorder};

              ${
                ownerState.variant === 'plain' &&
                css`
                  background-color: transparent;
                `
              }

              ${
                ownerState.variant === 'outlined' &&
                css`
                  background-color: ${overrideTheme.vars.palette.neutral['100']};
                `
              }
            `
          }
        `,
      },
    },
    JoyFormHelperText: {
      styleOverrides: {
        root: () => css`
          font-family: 'Gudea', sans-serif;
          font-size: 14px;
          font-style: normal;
          font-weight: 400;
          line-height: 133%;
        `,
      },
    },
    JoyTable: {
      styleOverrides: {
        root: ({ theme: overrideTheme }) => css`
          thead {
            td {
              font-size: 14px;
              font-weight: 700;
              white-space: normal;
              vertical-align: middle;
              border-bottom: 2px solid ${overrideTheme.palette.divider};
            }
          }

          tbody {
            tr {
              &:hover {
                cursor: pointer;
              }

              td {
                border-bottom: 1px solid ${overrideTheme.palette.divider};
                background-color: ${overrideTheme.vars.palette.background.surface};
              }

              th {
                transition: border-bottom-color 100ms ease;
              }

              td,
              &:not(:last-of-type) > td,
              &:not(:last-of-type) > th[scope='row'] {
                border-bottom-style: solid;
                border-bottom-width: 1px;
              }
            }
          }
        `,
      },
      defaultProps: {
        hoverRow: true,
        noWrap: true,
      },
    },
    JoyTextarea: {
      defaultProps: {
        size: 'lg',
        variant: 'plain',
      },
      styleOverrides: {
        root: ({ ownerState }) => css`
          padding-block: 0;
          background-color: #ffffff;

          ${
            ownerState.variant === 'outlined' &&
            css`
              box-shadow: none;
            `
          }
        `,
      },
    },
    JoyLink: {
      styleOverrides: {
        root: css`
          font-weight: bold;
          color: black;
        `,
      },
    },
    JoyModalDialog: {
      defaultProps: {
        variant: 'soft',
        color: 'neutral',
      },
      styleOverrides: {
        root: ({ theme: overrideTheme, ownerState }) => css`
          --Card-radius: ${overrideTheme.vars.radius.xl};
          row-gap: 1.5rem;

          ${
            ownerState.variant === 'soft' &&
            ownerState.color === 'neutral' &&
            css`
              background-color: rgba(248, 248, 248, 1);
              backdrop-filter: blur(8px);
            `
          }
        `,
      },
    },
    JoySelect: {
      defaultProps: {
        size: 'lg',
        variant: 'plain',
        slotProps: { button: { tabIndex: 0 } },
      },
      styleOverrides: {
        root: ({ theme: overrideTheme, ownerState }) => css`
          background-color: ${overrideTheme.palette.common.white};

          ${
            ownerState.variant === 'outlined' &&
            css`
              box-shadow: none;
              --joy-focus-thickness: 2px;
            `
          }

          ${
            ownerState.size === 'lg' &&
            css`
              height: 48px;
            `
          }
        `,
      },
    },
    JoySheet: {
      defaultProps: {
        variant: 'soft',
        color: 'neutral',
      },
      styleOverrides: {
        root: ({ theme: overrideTheme, ownerState }) => css`
          ${
            ownerState.variant === 'outlined' &&
            css`
              --variant-borderWidth: 2px;
            `
          }

          ${
            ownerState.color === 'neutral' &&
            ownerState.variant === 'outlined' &&
            css`
              background-color: ${overrideTheme.vars.palette.common.white};
            `
          }

          ${
            ownerState.color === 'neutral' &&
            ownerState.variant === 'soft' &&
            css`
              background-color: rgba(248, 248, 248, 0.75);
              backdrop-filter: blur(8px);
              box-shadow:
                -1px -1px 0 0 rgba(255, 255, 255, 0.8),
                1px 1px 0 0 rgba(109, 109, 109, 0.1),
                0 0 8px 0 rgba(109, 109, 109, 0.15);
            `
          }
        `,
      },
    },
    JoySnackbar: {
      defaultProps: {
        variant: 'soft',
        color: 'neutral',
      },
      styleOverrides: {
        root: ({ ownerState }) => css`
          ${
            ownerState.color === 'neutral' &&
            ownerState.variant === 'soft' &&
            css`
              background-color: rgba(240, 240, 240, 1);
              font-weight: 700;
              box-shadow:
                0 6px 12px -2px rgba(21, 21, 21, 0.08),
                0 2px 8px -2px rgba(21, 21, 21, 0.08);
            `
          }
        `,
      },
    },
    JoyAccordionSummary: {
      defaultProps: {
        color: 'neutral',
      },
      styleOverrides: {
        root: () => css`
          font-size: 18px;
          font-weight: 700;
          line-height: 30px;
        `,
      },
    },
    JoyCheckbox: {
      defaultProps: {
        slotProps: { input: { tabIndex: 0 } },
      },
    },
  },
});
